'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { Button, Modal } from '@insta-club/ui';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

interface PopUp {
    id: number;
    title: string;
    description: string;
    location: string;
    link: string | null;
    image: string;
}

interface PopUpsResponse {
    data: PopUp[];
    success: boolean;
}

export default function PopupsModal() {
    const pathName = usePathname();
    const includedPaths = ['/dashboard', '/analysis', '/explore', '/academy', '/profile'];
    const isIncludedPaths = includedPaths.includes(pathName);

    const currentLocation = pathName.substring(1);

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [filteredPopUps, setFilteredPopUps] = useState<PopUp[]>([]);

    const { data, isLoading } = useSWR<PopUpsResponse>(isIncludedPaths ? ApiKeys.GET_POP_UPS : null, (url: string) =>
        fetcher(url, 'dashboard', 'GET')
    );

    useEffect(() => {
        if (data?.data && Array.isArray(data.data)) {
            const filtered = data.data.filter((popup) => popup.location === currentLocation);
            setFilteredPopUps(filtered);

            if (filtered.length > 0) {
                setIsOpen(true);
                setCurrentIndex(0);
            }
        }
    }, [data, currentLocation]);

    const handleNext = () => {
        if (currentIndex < filteredPopUps.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleClose = async () => {
        try {
            setLoading(true);

            for (let i = 0; i <= currentIndex; i++) {
                await fetcher(ApiKeys.SEEN_POP_UPS(filteredPopUps[i].id), 'dashboard', 'GET');
            }

            setIsOpen(false);
            setCurrentIndex(0);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    if (!isIncludedPaths) return null;

    if (isLoading || filteredPopUps.length === 0) return null;

    const currentPopUp = filteredPopUps[currentIndex];
    const isLastPopUp = currentIndex === filteredPopUps.length - 1;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title=''
            // title='اعلان اینستاکلاب'
            description=''
            closeOnOverlayClick={false}
        >
            <div className='space-y-6'>
                <div className='w-full overflow-hidden rounded-xl'>
                    <Image
                        src={currentPopUp?.image ?? '/img-not-found-placeholder.jpg'}
                        alt='Popup announcement'
                        className='h-48 w-full object-cover'
                        width={1}
                        height={1}
                        unoptimized
                    />
                </div>

                <div className='space-y-2'>
                    <h3 className='text-base font-bold text-gray-800'>{currentPopUp?.title}</h3>
                    <p className='text-sm leading-6 text-gray-600'>{currentPopUp?.description}</p>
                </div>

                {currentPopUp?.link && (
                    <Link
                        href={currentPopUp.link}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-blue-900 transition-colors hover:bg-gray-200'
                    >
                        مشاهده لینک
                        <svg width='12' height='12' viewBox='0 0 13 13' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path
                                d='M0.5 0.5L12.1667 12.1667'
                                stroke='currentColor'
                                strokeMiterlimit='10'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                            <path
                                d='M0.5 9.05833V0.5H9.05833'
                                stroke='currentColor'
                                strokeMiterlimit='10'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </svg>
                    </Link>
                )}

                {filteredPopUps.length > 1 && (
                    <div className='text-center text-xs text-gray-500'>
                        {currentIndex + 1} از {filteredPopUps.length}
                    </div>
                )}

                <div className='flex gap-2'>
                    {filteredPopUps.length > 1 && currentIndex > 0 && (
                        <Button onClick={handlePrevious} fullWidth size='sm' variant='outline'>
                            قبلی
                        </Button>
                    )}
                    <Button loading={loading} onClick={isLastPopUp ? handleClose : handleNext} fullWidth size='sm'>
                        {isLastPopUp ? 'متوجه شدم' : 'بعدی'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

